import { FC, useMemo } from 'react';
import { exportChartToPdfProps } from '@frontend/analytics-shared';
import { BarChartAppearance } from '@frontend/charts';
import { useIsSmallMode } from '@frontend/shared';
import { useLocation } from '@frontend/weave-router';
import { usePhoneAnalyticsShallowStore } from '../hooks/use-phone-analytics-store';
import { CallAnswerRateChart } from './charts/call-answer-rate-chart';
import { HourlyInsightsChart } from './charts/hourly-insights-chart';
import { IncomingCallVolumeBreakdownChart } from './charts/incoming-call-volume-breakdown-chart';
import { PhoneExtensionChart } from './charts/phone-extension-chart';
import { ChartProps } from './charts/shared-types';
import { TotalCallVolumeChart } from './charts/total-call-volume-chart';
import { SmallModePanelSummary } from './small-mode-panel-summary';

interface Props {
  defaultChartAppearance?: Partial<BarChartAppearance>;
}

export const CallAnalyticsPanel: FC<React.PropsWithChildren<Props>> = ({ defaultChartAppearance }) => {
  const { isSmallMode } = useIsSmallMode();
  const { pathname } = useLocation();
  const { accessibleLocationsNames, filters } = usePhoneAnalyticsShallowStore('accessibleLocationsNames', 'filters');
  const isOpenHoursOnly = filters.open_office;
  const showAllMetrices = !isSmallMode || pathname.endsWith('/call-volume');

  const exportPdfProps = useMemo(
    () => exportChartToPdfProps(filters.location_id || [], accessibleLocationsNames, !isSmallMode),
    [isSmallMode, filters.location_id, accessibleLocationsNames]
  );

  const commonProps: ChartProps = {
    defaultChartAppearance,
    exportPdfProps,
    isSmallMode,
  };

  if (showAllMetrices) {
    return (
      <>
        {isSmallMode && <SmallModePanelSummary type='callVolume' />}
        <CallAnswerRateChart {...commonProps} />
        <TotalCallVolumeChart {...commonProps} />
        <PhoneExtensionChart {...commonProps} />
        {!isOpenHoursOnly && <HourlyInsightsChart {...commonProps} />}
        <IncomingCallVolumeBreakdownChart {...commonProps} />
      </>
    );
  }

  return (
    <>
      <PhoneExtensionChart {...commonProps} />
      {!isOpenHoursOnly && <HourlyInsightsChart {...commonProps} />}
      <IncomingCallVolumeBreakdownChart {...commonProps} />
    </>
  );
};
